import React, { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import Modal from "./Modal";
import { cn } from "@/lib/utils";
import type { Definition, Shift } from "@/types";
import type {
    AffectedShiftRow,
    ConstraintAnalysis,
    ScoreAnalysisModalProps,
} from "./ScoreAnalysisModal.types";

const constraintNameOverrides: Record<string, string> = {
    "equityEmployeeRole": "Balancement des quarts selon l'objectif de ratio en pourcentage de la table d'équité",
    "equityEmployeeRoleDay": "Balancement des quarts selon la quantité maximale désirée de la table d'équité",
    "generalEquity-soft": "Equity pour roles (facultatif)",
    "generalEquity-hard": "Equity pour roles (obligatoire)",
};

// Fallback labels for constraint names emitted by the solver but absent from
// `ConstraintDefinition.constraintDefinitions` (backend `/constraints/def`).
// Source of truth remains the backend — this table only fills the gap. Keep in
// sync if you add or rename a constraint in `EmployeeSchedulingConstraintProvider`.
const internalConstraintNames: Record<string, string> = {
    "equityEmployeeTableMax": "Balancement selon la quantité maximale désirée",
    "equityEmployeeTableMaxHard": "Balancement selon la quantité maximale désirée (obligatoire)",
    "equityEmployeeRoleDayHard": "Équité entre les employés pour un même rôle et jour (obligatoire)",
    "maxRoles-beta1": "Quantité de quarts maximale par plage horaire pour un ou plusieurs rôles et utilisateurs",
    "preferredShiftAmountMax": "Quantité maximale de quarts pour la période",
    "preferredShiftMax": "Quantité maximale de quarts",
    "equityDay": "Équité de jour",
    "ifxtheny_trigg": "Doit faire les 'quarts requis' si assigné au quart spécifié (déclencheur)",
    "assignTriggers": "Déclencheurs d'assignation",
    "preferredShiftRatioPerRole": "Ratio de quarts préféré par rôle",
};

// Solver may emit "<name>-HARD" / "<name>-SOFT" (priorityType suffix).
// Two variants of the same base name then share a row each → suffix the label
// so the user can tell them apart at a glance, not only via the Type column.
const resolveDisplayName = (
    name: string,
    definitions: Definition[] | null | undefined,
): string => {
    const suffixMatch = name.match(/-(HARD|SOFT)$/i);
    const baseName = suffixMatch ? name.slice(0, -suffixMatch[0].length) : name;
    const base = constraintNameOverrides[baseName]
        ?? definitions?.find((e) => e.name === baseName)?.displayName
        ?? internalConstraintNames[baseName]
        ?? baseName;

    if (!suffixMatch) {
        return base;
    }

    return suffixMatch[1].toUpperCase() === "HARD" ? `${base} (obligatoire)` : `${base} (flexible)`;
};

const getAffectedShiftRows = (constraint: ConstraintAnalysis): AffectedShiftRow[] => {
    const rows: AffectedShiftRow[] = [];

    for (const match of constraint.matches ?? []) {
        const justification = match.justification;
        if (!justification?.shift?.id) {
            continue;
        }
        rows.push({
            shift: justification.shift,
            score: justification.score ?? String(match.score ?? ""),
        });
    }

    return rows;
};

const shiftEmployeeName = (shift: Shift): string =>
    shift.employee ? shift.employee.name : shift.mandatory ? "Bris de service" : "À combler";

const shiftTimeRange = (shift: Shift): string => {
    const date = shift.start?.slice(0, 10) ?? "";
    const start = shift.start?.slice(11, 16) ?? "";
    const end = shift.end?.slice(11, 16) ?? "";
    const range = [start, end].filter(Boolean).join(" -");
    return [date, range].filter(Boolean).join(" ");
};

const ScoreAnalysisModal: React.FC<ScoreAnalysisModalProps> = (
    {
        open,
        onClose,
        score,
        analysis,
        fallbackMessage,
        definitions,
        onShiftClick,
    }) => {
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const toggleRow = (rowId: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) {
                next.delete(rowId);
            } else {
                next.add(rowId);
            }
            return next;
        });
    };

    useEffect(() => {
        setExpandedRows(new Set());
    }, [analysis]);

    const renderIcon = (c: ConstraintAnalysis) => {
        if (c.type === "hard" && (c.implicitScore ?? 0) < 0) {
            return (
                <svg className="h-4 w-4 text-red-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                    <path fillRule="evenodd"
                          d="M10 2a1 1 0 01.894.553l7 14A1 1 0 0117 18H3a1 1 0 01-.894-1.447l7-14A1 1 0 0110 2zm0 4a.75.75 0 00-.75.75v4.5a.75.75 0 001.5 0v-4.5A.75.75 0 0010 6zm0 8a1 1 0 100 2 1 1 0 000-2z"
                          clipRule="evenodd"/>
                </svg>
            );
        }
        if ((c.matches?.length ?? 0) === 0) {
            return (
                <svg className="h-4 w-4 text-green-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                    <path fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"/>
                </svg>
            );
        }
        return null;
    };

    const titleContent = (
        <>
            Analyse des résultats{" "}
            <span className="font-normal">{score ? `(${score})` : ""}</span>
        </>
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={titleContent}
            size="xl"
            footer={
                <button
                    onClick={onClose}
                    className="inline-flex items-center rounded-full bg-violet-600 cursor-pointer px-4 py-2 font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    Fermer
                </button>
            }
        >
            {!analysis || analysis.constraints.length === 0 ? (
                <div>{fallbackMessage || "Aucune contrainte à afficher."}</div>
            ) : (
                <table className="min-w-full table-fixed text-sm">
                    <thead>
                    <tr className="border-b">
                        <th className="w-7 px-1 py-2"></th>
                        <th className="w-8 px-2 py-2"></th>
                        <th className="px-2 py-2 text-left">Contrainte</th>
                        <th className="px-2 py-2 text-left">Type</th>
                        <th className="px-2 py-2 text-center"># Occurence</th>
                        <th className="px-2 py-2 text-right">Poids</th>
                        <th className="px-2 py-2 text-right">Score</th>
                    </tr>
                    </thead>
                    <tbody>
                    {analysis.constraints.map((c, i) => {
                        const displayName = resolveDisplayName(c.name, definitions);
                        const key = `${c.name}-${c.weight}-${c.score}`;
                        const rowId = `constraint-row-${i}`;
                        const affected = getAffectedShiftRows(c);
                        const isExpandable = affected.length > 0;
                        const isOpen = expandedRows.has(rowId);
                        let typeLabel = "Flex.";
                        if (c.type === "hard") {
                            typeLabel = "Oblig.";
                        } else if (c.type === "medium") {
                            typeLabel = "Flex. (M)";
                        }
                        return (
                            <React.Fragment key={key}>
                                <tr
                                    className={cn(
                                        "border-b hover:bg-gray-50",
                                        isExpandable && "cursor-pointer",
                                    )}
                                    onClick={isExpandable ? () => toggleRow(rowId) : undefined}
                                >
                                    <td className="w-7 px-1 py-2 text-center align-middle">
                                        {isExpandable && (
                                            <button
                                                type="button"
                                                aria-expanded={isOpen}
                                                aria-label={isOpen ? "Masquer les quarts affectés" : "Afficher les quarts affectés"}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleRow(rowId);
                                                }}
                                                className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                                            >
                                                {isOpen
                                                    ? <Minus size={14} aria-hidden />
                                                    : <Plus size={14} aria-hidden />}
                                            </button>
                                        )}
                                    </td>
                                    <td className="px-2 py-2 text-center align-middle">{renderIcon(c)}</td>
                                    <td className="px-2 py-2 align-middle text-left">{displayName}</td>
                                    <td className="px-2 py-2 align-middle text-left">{typeLabel}</td>
                                    <td className="px-2 py-2 align-middle text-center font-semibold">{c.matches?.length ?? 0}</td>
                                    <td className="px-2 py-2 align-middle text-right">{c.weight}</td>
                                    <td className="px-2 py-2 align-middle text-right">{c.implicitScore}</td>
                                </tr>
                                {isOpen && affected.map((row, j) => (
                                    <tr
                                        key={`${rowId}-shift-${j}`}
                                        className={cn(
                                            "border-b border-gray-100 bg-gray-50/60",
                                            onShiftClick && "cursor-pointer hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600",
                                        )}
                                        onClick={onShiftClick ? () => onShiftClick(row.shift) : undefined}
                                        title={onShiftClick ? "Afficher ce quart dans l'horaire" : undefined}
                                        role={onShiftClick ? "button" : undefined}
                                        aria-label={onShiftClick
                                            ? `Afficher ${row.shift.roleName} du ${shiftTimeRange(row.shift)} dans l'horaire`
                                            : undefined}
                                        tabIndex={onShiftClick ? 0 : undefined}
                                        onKeyDown={onShiftClick ? (e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                onShiftClick(row.shift);
                                            }
                                        } : undefined}
                                    >
                                        <td></td>
                                        <td></td>
                                        <td colSpan={4} className="px-2 py-1 align-middle">
                                            <div className="grid grid-cols-[10rem_12rem_1fr] items-baseline gap-x-3 animate-in fade-in slide-in-from-top-1 duration-150">
                                                <span className="font-medium text-gray-700">{row.shift.roleName}</span>
                                                <span className="text-gray-700">{shiftEmployeeName(row.shift)}</span>
                                                <span className="text-gray-500">{shiftTimeRange(row.shift)}</span>
                                            </div>
                                        </td>
                                        <td className="px-2 py-1 text-right align-middle text-gray-600">{row.score}</td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        );
                    })}
                    </tbody>
                </table>
            )}
        </Modal>
    );
};

export default ScoreAnalysisModal;
